import React, { useState, useEffect, useMemo } from 'react'
import { Row, Col, Form, Select } from 'antd'
import DatePicker from 'components/Common/DayjsDatePicker'
import { ShopOutlined } from '@ant-design/icons'
import { SelectPrefixWrapper } from './../Widgets'
import { CalendarIcon, LocationIcon } from '@slerp/assets'
import { ApolloQueryResult, useLazyQuery } from '@apollo/client'
import { useSlerp } from '@slerp/client'
import {
  QUERY_LOYALTY_CUSTOMERS_AGGREGATES,
  QUERY_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES,
  QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_AGGREGATES,
  QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_GET_LOYALTY_CUSTOMERS_LIST
} from './LoyaltyQueries'
import dayjs, { Dayjs } from 'dayjs'
import Loading from './../Utils/Loading'
import { CSVLink } from 'react-csv'
import { isNull } from 'lodash'
import { Decimal } from 'decimal.js'
import TopLoyaltyCustomers from './TopLoyaltyCustomers'
import LoyaltyStats from './LoyaltyStats'
import { LoyaltyCustomerListItem } from './types'
import { useMerchant } from 'hooks/useGlobalContext'

const { RangePicker } = DatePicker
const dateFormat = 'DD/MM/YYYY'
const queryDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss'

export type LoyaltyCustomer = {
  id: string
  first_name: string
  last_name: string
  email: string
  marketing_opt_in: boolean
  stamps: {
    aggregate: {
      count: number
    }
  }
  redeemed_rewards: {
    aggregate: {
      count: number
    }
  }
  orders: {
    aggregate: {
      sum: {
        total: number
      }
    }
  }
}

type CSVCustomerData = {
  customer: string
  email: string
  current_stamps: number
  current_rewards: number
  rewards_redeemed_online: number
  rewards_redeemed_in_store: number
  all_time_rewards_redeemed_online: number
  all_time_rewards_redeemed_in_store: number
  lifetime_stamps_online: number
  lifetime_stamps_in_store: number
  total_spent: number
}

type StoreType = {
  id: string
  name: string
  archived_at: null | string
}

type AggregateType = {
  aggregate: {
    count: number
  }
}

type AggregateSumTotalType = {
  aggregate: {
    count: number
    sum: {
      total: number
    }
  }
}

interface LoyaltyAggregatesType {
  stamps?: AggregateType
  redeemed_rewards?: AggregateType
  orders?: AggregateSumTotalType
}

type SortDirection = 'asc' | 'desc'
type SortBy =
  | 'stamps'
  | 'customer_all_time_rewards'
  | 'customer_rewards'
  | 'orders'

export interface HandleTableSort {
  sortBy: SortBy
  sortDirection: SortDirection
  id: string
}

const LoyaltyOverview = () => {
  const { merchantLocations } = useMerchant()
  const [startDate, setStartDate] = useState<Dayjs>(
    dayjs().subtract(7, 'days')
  )
  const [endDate, setEndDate] = useState<Dayjs>(dayjs())
  const [storeIds, setStoreIds] = useState<string[] | []>([])
  const [optInFilter, setOptInFilter] = useState(false)
  const [hasDateChanged, setHasDateChanged] = useState(false)
  const [activeSort, setActiveSort] = useState('total-spent-desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [customers, setCustomers] = useState([])
  const [loyaltyCustomersList, setLoyaltyCustomersList] = useState<
    LoyaltyCustomerListItem[]
  >([])
  const [customerAggregateQuery, setCustomerAggregateQuery] = useState(
    QUERY_LOYALTY_CUSTOMERS_AGGREGATES
  )
  const [selectedDropdownOption, setSelectedDropdownOption] =
    useState<string>('all_stores')
  const [loyaltySource, setLoyaltySource] = useState<string | undefined>(
    undefined
  )

  const changeDates = (dates: Array<Dayjs>) => {
    const [changedStartDate, changedEndDate] = dates

    if (startDate !== changedStartDate) {
      setStartDate(changedStartDate)
      setHasDateChanged(true)
    }
    if (endDate !== changedEndDate) {
      setEndDate(changedEndDate)
      setHasDateChanged(true)
    }
  }

  const { user } = useSlerp()

  useEffect(() => {
    if (merchantLocations.length > 0) {
      loadCustomersAggregate()

      const storeDataIds = merchantLocations.map(({ id }: StoreType) => id)
      setStoreIds(storeDataIds)
    }
  }, [merchantLocations])

  useEffect(() => {
    let loyaltyAggregateQuery = null

    switch (selectedDropdownOption) {
      case 'all_stores':
        loyaltyAggregateQuery = optInFilter
          ? QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_AGGREGATES

        break

      case 'bulk_operation':
        loyaltyAggregateQuery = optInFilter
          ? QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES
        break

      default:
        loyaltyAggregateQuery = optInFilter
          ? QUERY_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_LOYALTY_CUSTOMERS_AGGREGATES
        break
    }

    if (loyaltyAggregateQuery) {
      setCustomerAggregateQuery(loyaltyAggregateQuery)
    }
  }, [selectedDropdownOption, optInFilter])

  const [
    loadCustomersAggregate,
    {
      data: loyaltyCustomersAggregateData,
      refetch: loyaltyCustomersAggregateRefetch,
      loading: loyaltyCustomersAggregateLoading
    }
  ] = useLazyQuery(customerAggregateQuery, {
    variables: {
      merchantId: user?.merchant?.id ?? '',
      startDate: startDate.startOf('day').format(queryDateTimeFormat),
      endDate: endDate.endOf('day').format(queryDateTimeFormat),
      storeIds: storeIds
    },
    onCompleted: () => {
      loadLoyaltyCustomersList()
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (
      loyaltyCustomersAggregateData &&
      loyaltyCustomersAggregateData.customers
    ) {
      const filtered_customers = loyaltyCustomersAggregateData.customers.filter(
        (customer: LoyaltyCustomer) =>
          customer.stamps.aggregate.count !== 0 ||
          customer.redeemed_rewards.aggregate.count !== 0
      )
      setCustomers(filtered_customers)
    }
  }, [loyaltyCustomersAggregateData])

  const [
    loadLoyaltyCustomersList,
    {
      data: loyaltyCustomersListData,
      refetch: refetchLoyaltyCustomersList,
      loading: loyaltyCustomersListLoading
    }
  ] = useLazyQuery(QUERY_GET_LOYALTY_CUSTOMERS_LIST, {
    variables: {
      merchantId: user?.merchant?.id ?? '',
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      storeIds: storeIds,
      customerIds: (customers || []).map(({ id }) => id),
      optInFilter,
      limit: 100,
      offset: 0,
      sortBy: 'orders',
      sortDirection: 'desc'
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    setLoyaltyCustomersList(
      loyaltyCustomersListData?.loyaltyCustomersList ?? []
    )
  }, [loyaltyCustomersListData])

  const filteredLoyaltyCustomersList = useMemo(() => {
    if (!loyaltySource) return loyaltyCustomersList
    if (loyaltySource === 'in_store')
      return loyaltyCustomersList.filter(
        (c) =>
          (c.periodStampsInStore ?? 0) > 0 || (c.periodRewardsInStore ?? 0) > 0
      )
    if (loyaltySource === 'online')
      return loyaltyCustomersList.filter(
        (c) =>
          (c.periodStampsOnline ?? 0) > 0 || (c.periodRewardsOnline ?? 0) > 0
      )
    return loyaltyCustomersList
  }, [loyaltyCustomersList, loyaltySource])

  const { stampsEarned, rewardsEarned, rewardsRedeemed, totalSpent } = useMemo(
    () =>
      filteredLoyaltyCustomersList.reduce(
        (acc, c) => ({
          stampsEarned:
            acc.stampsEarned +
            (c.periodStampsOnline ?? 0) +
            (c.periodStampsInStore ?? 0),
          rewardsEarned: acc.rewardsEarned + (c.currentRewards ?? 0),
          rewardsRedeemed:
            acc.rewardsRedeemed +
            (c.periodRewardsOnline ?? 0) +
            (c.periodRewardsInStore ?? 0),
          totalSpent: acc.totalSpent + (c.totalSpent ?? 0)
        }),
        { stampsEarned: 0, rewardsEarned: 0, rewardsRedeemed: 0, totalSpent: 0 }
      ),
    [filteredLoyaltyCustomersList]
  )

  const onCSVDownload = () => {
    const items: CSVCustomerData[] = filteredLoyaltyCustomersList.map((c) => ({
      customer: `${c.firstName} ${c.lastName}`,
      email: c.email,
      current_stamps: c.currentStamps ?? 0,
      current_rewards: c.currentRewards ?? 0,
      rewards_redeemed_online: c.periodRewardsOnline ?? 0,
      rewards_redeemed_in_store: c.periodRewardsInStore ?? 0,
      all_time_rewards_redeemed_online: c.lifetimeRewardsOnline ?? 0,
      all_time_rewards_redeemed_in_store: c.lifetimeRewardsInStore ?? 0,
      lifetime_stamps_online: c.lifetimeStampsOnline ?? 0,
      lifetime_stamps_in_store: c.lifetimeStampsInStore ?? 0,
      total_spent: c.totalSpent ?? 0
    }))
    if (items.length === 0) return ''
    const replacer = (_key: string, value: unknown) =>
      isNull(value) ? 0 : value
    const header = Object.keys(items[0]) as (keyof CSVCustomerData)[]
    const humanized_header = header.map((title) => title.replace(/_/g, ' '))
    return [
      humanized_header.join(','),
      ...items.map((row) =>
        header.map((field) => JSON.stringify(row[field], replacer)).join(',')
      )
    ].join('\r\n')
  }

  const handleStoreChange = (value: string) => {
    setSelectedDropdownOption(value)
    switch (value) {
      case 'all_stores':
        const storeDataIds = merchantLocations.map(({ id }: StoreType) => id)

        setStoreIds(storeDataIds)
        break

      case 'bulk_operation':
        setStoreIds([])
        break

      default:
        setStoreIds([value])
        break
    }
    setActiveSort('total-spent-desc')
    setCurrentPage(1)
  }

  // To-do: define query result type
  const handleTableSort = ({
    sortBy,
    sortDirection,
    id
  }: HandleTableSort): Promise<ApolloQueryResult<any>> | undefined => {
    if (!refetchLoyaltyCustomersList) return
    setActiveSort(id)
    return refetchLoyaltyCustomersList({
      sortBy,
      sortDirection
    })
  }

  return (
    <>
      <Row justify='space-between' className='_mt-16 _mb-0'>
        <Row gutter={8} className='_mb-0'>
          <Col className='orders-form-item _mb-0'>
            <SelectPrefixWrapper prefixicon={<LocationIcon />}>
              <Select
                placeholder='Filter by Location'
                onChange={handleStoreChange}
                defaultValue='all_stores'
                allowClear={false}
                className='control'
                style={{
                  minWidth: 'max-content'
                }}
                dropdownStyle={{
                  minWidth: '200px'
                }}
              >
                <Select.Option value={'all_stores'} key={'all_stores'}>
                  All locations
                </Select.Option>
                <Select.Option value={'bulk_operation'} key={'bulk_operation'}>
                  Bulk Operation
                </Select.Option>
                {merchantLocations.map((store: StoreType) => (
                  <Select.Option value={store.id} key={store.id}>
                    {store.name}
                  </Select.Option>
                ))}
              </Select>
            </SelectPrefixWrapper>
          </Col>
          <Col>
            <SelectPrefixWrapper prefixicon={<ShopOutlined />}>
              <Select
                placeholder='Loyalty source'
                onChange={(value: string) =>
                  setLoyaltySource(value === 'all' ? undefined : value)
                }
                defaultValue='all'
                allowClear={false}
                className='control'
                style={{ minWidth: 'max-content' }}
                dropdownStyle={{ minWidth: '220px' }}
              >
                <Select.Option value='all'>All sources</Select.Option>
                <Select.Option value='in_store'>
                  In store (offline)
                </Select.Option>
                <Select.Option value='online'>Online</Select.Option>
              </Select>
            </SelectPrefixWrapper>
          </Col>
          <Col>
            <Form.Item name='date' className='_mb-0'>
              <SelectPrefixWrapper prefixicon={<CalendarIcon />}>
                <RangePicker
                  format={dateFormat}
                  allowClear={false}
                  onChange={(e: any) => {
                    changeDates(e)
                  }}
                  onOpenChange={(open) => {
                    if (open) return
                    if (!hasDateChanged) return
                    if (
                      refetchLoyaltyCustomersList === undefined ||
                      loyaltyCustomersAggregateRefetch === undefined
                    )
                      return
                    refetchLoyaltyCustomersList({
                      startDate: dayjs(startDate)
                        .startOf('day')
                        .format('YYYY-MM-DD'),
                      endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DD')
                    })
                    loyaltyCustomersAggregateRefetch({
                      startDate: dayjs(startDate)
                        .startOf('day')
                        .format(queryDateTimeFormat),
                      endDate: dayjs(endDate)
                        .endOf('day')
                        .format(queryDateTimeFormat)
                    })
                    setHasDateChanged(false)
                    setActiveSort('total-spent-desc')
                    setCurrentPage(1)
                  }}
                  placeholder={['Start date', 'End date']}
                  defaultValue={[startDate, endDate]}
                  className='_pl-48'
                />
              </SelectPrefixWrapper>
            </Form.Item>
          </Col>
        </Row>
        <Col>
          <CSVLink
            data={onCSVDownload()}
            filename={`${
              user?.merchant?.slug
            }_customer_loyalty_stats_${Date.now()}.csv`}
            className='ant-btn'
            data-testid='customer-csv-export'
          >
            Export Customers
          </CSVLink>
        </Col>
      </Row>
      {!loyaltyCustomersAggregateLoading && !loyaltyCustomersListLoading ? (
        <LoyaltyStats
          loyaltyCustomers={filteredLoyaltyCustomersList.length}
          stampsEarned={stampsEarned}
          rewardsEarned={rewardsEarned}
          rewardsRedeemed={rewardsRedeemed}
          totalSpent={totalSpent}
          averageBasketSize={
            new Decimal(
              customers?.reduce((acc: number, cust: LoyaltyAggregatesType) => {
                if (cust?.orders?.aggregate?.sum?.total) {
                  return cust.orders.aggregate.sum.total + acc
                }
                return acc
              }, 0)
            )
              .dividedBy(
                new Decimal(
                  customers?.reduce(
                    (acc: number, cust: LoyaltyAggregatesType) => {
                      if (cust?.orders?.aggregate?.count) {
                        return cust.orders.aggregate.count + acc
                      }
                      return acc
                    },
                    0
                  )
                )
              )
              .toNumber() || 0
          }
        />
      ) : (
        <Loading />
      )}
      <TopLoyaltyCustomers
        loading={loyaltyCustomersListLoading}
        data={{ loyaltyCustomersList: filteredLoyaltyCustomersList }}
        customers={customers}
        toggleOptIn={() => setOptInFilter(!optInFilter)}
        optInFilter={optInFilter}
        customerAggregateRefetch={loyaltyCustomersAggregateRefetch}
        activeSort={activeSort}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        handleTableSort={handleTableSort}
        onStampSuccess={(customerId, stampsAdded) =>
          setLoyaltyCustomersList((list) =>
            list.map((c) =>
              c.id === customerId
                ? { ...c, currentStamps: (c.currentStamps ?? 0) + stampsAdded }
                : c
            )
          )
        }
      />
    </>
  )
}

export default LoyaltyOverview
